/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import GradingMethod from '@linc-technologies/linced-common/enums/grading-method';
import Scope from '@linc-technologies/linced-common/enums/scopes';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import { classNameBindings, classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

@classNames('assessment-module-grader', 'layout-row', 'layout-wrap')
@classNameBindings(
  'mini:assessment-module-grader--mini',
  'canSaveNumericGrade:md-dirty',
)
export default class AssessmentModuleGrader extends Component {
  // Services
  @service
  permissions;

  @service
  session;

  @service
  store;

  @service
  toaster;

  GradingMethod = GradingMethod;

  autosaveNumericGrades = false;

  @tracked usePlaceholderGrades = false;

  onChange() {}

  @alias('module.gradeSchedule.awardMethod')
  awardMethod;

  @computed('awardMethod')
  get isNumeric() {
    return this.get('awardMethod') === GradingMethod.NUMERIC;
  }

  // gradeIsFresh determines whether a grade was set today by the current user.
  @computed('grade.{createTime,assessor.id}', 'session.person')
  get gradeIsFresh() {
    let gradeDate = moment
      .unix(this.get('grade.createTime'))
      .format('DD/MM/YYYY');
    let today = moment().format('DD/MM/YYYY');
    if (gradeDate === today) {
      if (this.get('grade.assessor.id') === this.session.person.id) {
        return true;
      }
    }
    return false;
  }

  // canRevertGrade determines whether the current user can revert the grade.
  @computed('gradeIsFresh', 'session.person')
  get canRevertGrade() {
    return this.gradeIsFresh && this.permissions.meet(Scope.TENANT_STAFF);
  }

  @computed('usePlaceholderGrades', 'gradeIsFresh')
  get showPlaceholder() {
    return this.usePlaceholderGrades && !this.gradeIsFresh;
  }

  @computed(
    'grade',
    'usePlaceholderGrades',
    'awardMethod',
    'selectedOptionLabel',
    'gradeIsFresh',
  )
  get placeholder() {
    if (
      this.usePlaceholderGrades &&
      !isEmpty(this.grade) &&
      !this.gradeIsFresh
    ) {
      // There's a previous grade, and it's not from today / can't be
      // reverted - so should be used as the placeholder.
      switch (this.awardMethod) {
        case GradingMethod.SELECT:
          return this.selectedOptionLabel || this.get('grade.awarded');
        default:
          return this.get('grade.awarded');
      }
    }

    // Use generic placeholder text.
    switch (this.awardMethod) {
      case GradingMethod.SELECT:
        return 'Select grade';
      default:
        return 'Grade';
    }
  }

  @computed(
    'gradeIsFresh',
    'usePlaceholderGrades',
    'numericGrade',
    'grade.numericAwarded',
  )
  get numericGradeChanged() {
    if (isEmpty(this.numericGrade)) {
      // Never consider an empty field to be changed.
      return false;
    }
    if (this.usePlaceholderGrades && !this.gradeIsFresh) {
      // No saved grade for today; consider it changed, since it's non-empty.
      return true;
    }

    // The input field sets a stringy value, so we need to parse it as a float
    // in order to compare it to the existing grade.
    return parseFloat(this.numericGrade) !== this.get('grade.numericAwarded');
  }

  @computed('isNumeric', 'numericGradeChanged', 'validationMessage')
  get canSaveNumericGrade() {
    if (!this.isNumeric) {
      // Non-numeric grades aren't savable as numeric grades.
      return false;
    }
    // Can only be saved if it's changed and there are no validation messages.
    return this.numericGradeChanged && isEmpty(this.validationMessage);
  }

  @computed(
    'grade.multiSelectAwarded.[]',
    'module.gradeSchedule.select.values.[]',
  )
  get selectedOptions() {
    let selected = this.get('grade.multiSelectAwarded');
    let options = this.get('module.gradeSchedule.select.values');
    if (selected && selected.length) {
      return options.filter(function (option) {
        return selected.includes(option.get('value'));
      });
    }

    return [];
  }

  @computed('grade.selectAwarded', 'module.gradeSchedule.select.values.[]')
  get selectedOption() {
    if (isEmpty(this.get('grade.selectAwarded'))) {
      return;
    }

    let options = this.get('module.gradeSchedule.select.values') ?? [];
    return findBy(options, 'value', this.get('grade.selectAwarded'));
  }

  @alias('selectedOption.label')
  selectedOptionLabel;

  @computed('mini', 'module.gradeSchedule.select.values.[]')
  get wrapIconSelect() {
    if (this.mini) {
      // In "mini" mode (i.e. group grading), only wrap if there are lots of
      // values; they wrap too aggressively in table cells.
      return this.get('module.gradeSchedule.select.values.length') > 6;
    }
    // Not "mini" - allow wrapping.
    return true;
  }

  @observes('grade.numericAwarded', 'grade.numericScale')
  _watchNumericGrade() {
    if (!this.usePlaceholderGrades || this.gradeIsFresh) {
      this.numericGrade = this.get('grade.numericAwarded');
    }
  }

  init() {
    super.init(...arguments);
    let grade = this.grade;
    if (
      this.isNumeric &&
      grade &&
      (!this.usePlaceholderGrades || this.gradeIsFresh)
    ) {
      this.numericGrade = grade.get('numericAwarded');
    }
  }

  @tracked onInput = null;

  @tracked
  numericGrade;

  @action
  setNumericGrade(value) {
    this.numericGrade = value;
  }

  @tracked grade;

  @computed('onInput')
  get validationMessage() {
    if (!isEmpty(this.onInput)) {
      return this.onInput.target.validationMessage;
    }
  }

  @action
  maybeSaveNumericGrade(ev) {
    this.onInput = ev;
    if (this.canSaveNumericGrade) {
      // Grade is currently savable; check if we should do so.
      let isEnter = ev.type === 'keyup' && ev.key === 'Enter';
      let isBlur = ev.type === 'blur';
      if (isEnter || (isBlur && this.autosaveNumericGrades)) {
        this.saveNumericGrade();
      }
    }
  }

  @action
  async setSelectGrade(option) {
    let grade = await this.getGrade(GradingMethod.SELECT);

    grade.set('selectAwarded', option.get('value'));

    // Push audit reason in if editing an existing record.
    if (!grade.get('isNew') && !isEmpty(this.auditReason)) {
      grade.set('auditReason', this.auditReason);
    }

    grade.save().then(() => {
      this.grade = grade;
      this.onChange();
    });
  }

  @action
  async setIconGrade(option) {
    let grade = await this.getGrade(GradingMethod.ICON_SELECT);

    grade.set('iconAwarded', option.get('value'));

    // Push audit reason in if editing an existing record.
    if (!grade.get('isNew') && !isEmpty(this.auditReason)) {
      grade.set('auditReason', this.auditReason);
    }

    grade.save().then(() => {
      this.grade = grade;
      this.onChange();
    });
  }

  @action
  async toggleMultiSelectGrade(option) {
    let grade = await this.getGrade(GradingMethod.MULTIPLE_SELECT);
    let selected = grade.get('multiSelectAwarded');

    if (grade.get('isNew')) {
      // New grade object - push any already-selected options into it
      let existing = this.get('grade.multiSelectAwarded');
      if (existing) {
        existing.forEach(function (option) {
          selected.pushObject(option);
        });
      }
    } else if (!isEmpty(this.auditReason)) {
      // Push audit reason in if editing an existing record.
      grade.set('auditReason', this.auditReason);
    }

    // Add or remove the option which was selected
    let newValue = option.get('value');
    if (selected.includes(newValue)) {
      selected.removeObject(newValue);
    } else {
      selected.pushObject(newValue);
    }

    grade.save().then(() => {
      this.grade = grade;
      this.onChange();
    });
  }

  @action
  revertGrade() {
    let grade = this.grade;
    if (this.canRevertGrade) {
      grade.set('auditReason', 'Accidental grade reverted');
      grade
        .destroyRecord()
        .then(() => {
          this.toaster.success('Grade Reverted Successfully');
          if (this.usePlaceholderGrades && this.isNumeric) {
            // Clear grade input, allowing the placeholder to show the
            // previous grade (if applicable).
            this.numericGrade = '';
          }
          this.onChange(true);
        })
        .catch(() => {
          this.toaster.error('Error When Reverting Grade');
          grade.rollbackAttributes();
        });
    }
  }

  async getGrade(gradeType) {
    let latestGrade = this.grade;

    if (
      this.edit &&
      this.permissions.meet('urn:linced:organisation:role:admin')
    ) {
      if (latestGrade) {
        // Administrator editing an existing grade (may not be today or created by them).
        return latestGrade;
      } else if (this.date) {
        // Administrator adding a grade to a previous date via the assessment history table.
        return this.createGrade(gradeType, {
          createTime: moment(this.date, 'YYYY-MM-DD').unix(),
        });
      }
    }

    if (
      latestGrade &&
      latestGrade.get('assessor.id') === this.get('session.personId')
    ) {
      let gradeDate = moment
        .unix(latestGrade.get('createTime'))
        .format('YYYY-MM-DD');
      if (gradeDate === moment().format('YYYY-MM-DD')) {
        // Latest grade was today, so we can update it.
        return latestGrade;
      }
    }

    return this.createGrade(gradeType);
  }

  // createGrade awaits the module's assessment first: ember-data no longer
  // accepts an async belongsTo's PromiseProxy as a relationship value when
  // creating a record, it needs the resolved record.
  async createGrade(gradeType, props = {}) {
    let assessment = await this.get('module.assessment');
    return this.store.createRecord('assessments/grade', {
      schoolId: this.get('session.school'),
      assessmentId: assessment,
      assessmentModuleId: this.module,
      assessorId: this.get('session.person'),
      personId: this.person,
      gradeType,
      ...props,
    });
  }

  // Decorated directly rather than via a same-named delegating action: as
  // sibling class members the delegator and this implementation collided.
  @action
  async saveNumericGrade() {
    let grade = await this.getGrade(GradingMethod.NUMERIC);

    grade.set('numericAwarded', this.numericGrade);
    grade.set('numericScale', 0);

    // Push audit reason in if editing an existing record.
    if (!grade.get('isNew') && !isEmpty(this.auditReason)) {
      grade.set('auditReason', this.auditReason);
    }

    grade.save().then(() => {
      this.grade = grade;
      this.onChange();
    });
  }
}
